<template>
    <header
        class="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        :style="headerStyle"
    >
        <!-- Bottom glow line -->
        <div class="absolute bottom-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(167,93,237,0.4) 25%, rgba(232,121,249,0.6) 50%, rgba(167,93,237,0.4) 75%, transparent 100%);"></div>

        <div class="px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
            <div class="flex items-center justify-between h-16 lg:h-[70px]">

                <!-- Logo -->
                <a href="/" class="flex items-center gap-2.5 group flex-shrink-0">
                    <div class="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                         style="background: linear-gradient(135deg, #7c3aed 0%, #a75ded 50%, #e879f9 100%); box-shadow: 0 4px 20px rgba(167,93,237,0.5);">
                        <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2L2 7l10 5 10-5-10-5z" opacity="0.9"/>
                            <path d="M2 17l10 5 10-5" opacity="0.7"/>
                            <path d="M2 12l10 5 10-5"/>
                        </svg>
                    </div>
                    <span class="font-black text-lg sm:text-xl tracking-tight"
                          style="background: linear-gradient(135deg, #fff 0%, #c084fc 100%); -webkit-background-clip: text; background-clip: text; color: transparent;">
                        {{ appName }}
                    </span>
                </a>

                <!-- Right actions -->
                <div class="flex items-center gap-2 sm:gap-3">

                    <!-- NOT authenticated -->
                    <template v-if="!isAuthenticated">
                        <button
                            @click="openModal"
                            class="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-[1.05] active:scale-[0.97]"
                            style="background: linear-gradient(135deg, #7c3aed 0%, #a75ded 60%, #e879f9 100%); color: #fff; box-shadow: 0 4px 20px rgba(167,93,237,0.45);">
                            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                            </svg>
                            <span class="hidden xs:inline sm:inline">{{ t('connectWalletUpper') }}</span>
                            <span class="xs:hidden sm:hidden">Connect</span>
                        </button>
                    </template>

                    <!-- IS authenticated -->
                    <template v-else>
                        <Link
                            href="/user/investments"
                            class="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-[1.04] active:scale-95"
                            style="background: linear-gradient(135deg, #7c3aed 0%, #a75ded 100%); color: #fff; box-shadow: 0 4px 14px rgba(167,93,237,0.35);">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                            </svg>
                            Dashboard
                        </Link>

                        <div class="flex items-center gap-2 px-3 py-2 rounded-xl border cursor-default"
                             style="background: rgba(167,93,237,0.08); border-color: rgba(167,93,237,0.35);">
                            <div class="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
                                 style="background: #a75ded; box-shadow: 0 0 6px rgba(167,93,237,0.8);"></div>
                            <span class="text-white text-xs sm:text-sm font-semibold tracking-wide">{{ userDisplay }}</span>
                        </div>
                    </template>

                    <!-- Hamburger -->
                    <button
                        class="lg:hidden ml-1 p-2 rounded-lg transition-colors hover:bg-white/10"
                        @click="mobileMenuOpen = !mobileMenuOpen">
                        <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Mobile menu -->
            <Transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 -translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2"
            >
                <div v-if="mobileMenuOpen" class="lg:hidden border-t pb-4 pt-3 space-y-2"
                     style="border-color: rgba(167,93,237,0.15);">
                    <div class="pt-2" v-if="!isAuthenticated">
                        <button
                            @click="openModal; mobileMenuOpen = false"
                            class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all"
                            style="background: linear-gradient(135deg, #7c3aed, #a75ded, #e879f9); color: #fff; box-shadow: 0 4px 16px rgba(167,93,237,0.4);">
                            {{ t('connectWalletUpper') }}
                        </button>
                    </div>
                    <div class="pt-2" v-else>
                        <Link href="/user/investments"
                              class="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all"
                              style="background: linear-gradient(135deg, #7c3aed, #a75ded); color: #fff;">
                            Dashboard
                        </Link>
                    </div>
                </div>
            </Transition>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { usePage, Link } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import { useWalletModal } from '@/composables/useWalletModal'

const page = usePage()
const { t } = useTranslation()
const { openModal } = useWalletModal()

const authUser        = computed(() => page.props.auth?.user || null)
const isAuthenticated = computed(() => !!authUser.value)
const appName         = computed(() => page.props.appName || 'TokenHive')

const userDisplay = computed(() => {
    if (!authUser.value) return ''
    const addr = authUser.value.wallet_address
    if (addr) return `${addr.slice(0, 6)}...${addr.slice(-4)}`
    return authUser.value.name?.split(' ')[0] || 'User'
})

const scrolled = ref(false)
const mobileMenuOpen = ref(false)

const onScroll = () => { scrolled.value = window.scrollY > 30 }
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

const headerStyle = computed(() => ({
    background: scrolled.value ? 'rgba(7,3,15,0.97)' : 'rgba(7,3,15,0.6)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    boxShadow: scrolled.value ? '0 4px 30px rgba(167,93,237,0.12)' : 'none',
}))
</script>

<style scoped>
@media (prefers-reduced-motion: reduce) {
    .transition-all, .transition-colors, .transition-transform { transition: none !important; }
}
</style>
